import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, Building2, ArrowRightLeft, Link2,
  Users, UserCheck, UserX, UserPlus, Search,
  ChevronRight, Clock, CheckCircle2, XCircle,
  AlertTriangle, Send, Eye, Plus, ArrowLeftRight,
  Activity, ShieldCheck, RefreshCw, ArchiveRestore,
  ArrowUpRight, ArrowDownLeft, Banknote,
  History, Loader2, X, Check, Building,
  CircleDot, ChevronLeft, GraduationCap,
  Signal,
  CreditCard,
  BookOpenText,
  ShieldAlert
} from 'lucide-react';
import { api } from '../services/api';
import EleveTransfersTab from './EleveTransfersTab';
import InterSiteFamillesDashboard from './InterSiteFamillesDashboard';
import CoherenceInterSitePanel from './interSite/CoherenceInterSitePanel';
import CoherenceTransfertsPanel from './interSite/CoherenceTransfertsPanel';
import ModalWrapper from './interSite/InterSiteModal';
import PageDetailInterSite from './interSite/PageDetailInterSite';
import SiteTag from './interSite/SiteTag';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import LoadingSpinner from './LoadingSpinner';
import GuideUtilisationDrawer from './GuideUtilisationDrawer';
import { Toast } from './ui/Toast';
import {
  PAGE_SIZE_AUDIT,
  PAGE_SIZE_CARDS,
  PAGE_SIZE_TABLE,
  PaginationBar,
  SearchableSelect,
  TransferExecutionOverlay,
  usePaginatedList
} from './interSite/InterSiteUi';

// Dimensions issues du modèle visuel. Les hauteurs ne se redéfinissent pas à la
// main : h-9 pour une action principale ou un pied de modal, h-8 dans une ligne
// de tableau.
const BOUTON_PIED = 'flex-1 h-9 min-w-[168px] rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
const BOUTON_PLEIN = 'bg-[#246990] text-white hover:bg-[#1f5a7a]';
const BOUTON_SECONDAIRE = 'border border-gray-200 text-gray-600 hover:bg-gray-50';
const BOUTON_DANGER = 'bg-red-600 text-white hover:bg-red-700';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES & HELPERS
// ─────────────────────────────────────────────────────────────────────────────
// Un badge répété d'une ligne à l'autre porte une largeur fixe, faute de quoi la
// colonne « Statut » ondule au gré de la longueur des libellés.
const BADGE = 'inline-flex items-center justify-center gap-1.5 w-[132px] h-6 rounded-full text-xs font-medium whitespace-nowrap';

// Palette de sens du modèle visuel : teal pour une étape franchie, emerald
// réservé aux montants accordés, amber pour l'attente, red pour le refus.
const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved_source: { label: 'Départ validé', color: 'bg-[#e7f3f8] text-[#246990]', icon: CheckCircle2 },
  approved_both: { label: 'Double validation', color: 'bg-teal-100 text-teal-700', icon: ShieldCheck },
  executed: { label: 'Exécuté', color: 'bg-teal-100 text-teal-700', icon: CheckCircle2 },
  rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-700', icon: XCircle },
  completed: { label: 'Complété', color: 'bg-teal-100 text-teal-700', icon: CheckCircle2 },
  failed: { label: 'Échoué', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const TX_STATUS = {
  completed: { label: 'Complété', color: 'bg-teal-100 text-teal-700', icon: CheckCircle2 },
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  failed: { label: 'Échoué', color: 'bg-red-100 text-red-700', icon: XCircle },
};

// Statuts des liaisons enfants à charge, au même format.
const LIAISON_STATUS = {
  active: { label: 'Active', color: 'bg-teal-100 text-teal-700', icon: CheckCircle2 },
  suspended: { label: 'Suspendue', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  restore: { label: 'À réactiver', color: 'bg-[#e7f3f8] text-[#246990]', icon: UserPlus },
  terminated: { label: 'Terminée', color: 'bg-gray-100 text-gray-600', icon: XCircle },
};

const fmt = n => `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n || 0)} FCFA`;
const fmtDT = d => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const getApiError = (res, fallback = 'Une erreur est survenue') => res?.error || res?.message || fallback;
const normalizeText = value => String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const siteLabel = value => value || 'Site non renseigné';
const recentDate = item => item?.executed_at || item?.completed_at || item?.updated_at || item?.created_at;
const sortRecent = items => [...(items || [])].sort((a, b) => new Date(recentDate(b) || 0) - new Date(recentDate(a) || 0));
const hasTechnicalIds = value => /#\d+|\b(id|uuid)\b/i.test(String(value || ''));
const transactionReadableDescription = tx => {
  const source = tx.source_site || tx.site_source || tx.source_site_subdomain || 'site source';
  const target = tx.target_site || tx.site_cible || tx.target_site_subdomain || 'site cible';
  const parent = tx.nom_employe || tx.parent_name || tx.employee_name || tx.employe_nom_complet;
  const child = tx.nom_eleve || tx.child_name || tx.eleve_name || tx.eleve_nom_complet;
  const period = tx.mois_concerne ? ` · ${tx.mois_concerne}` : '';
  const classe = tx.child_classe || tx.classe ? ` · ${tx.child_classe || tx.classe}` : '';

  if (tx.description_lisible && !hasTechnicalIds(tx.description_lisible)) return tx.description_lisible;
  if (parent || child) return `Retenue inter-sites : ${parent || 'Employé'} (${source}) → ${child || 'Élève'} (${target})${classe}${period}`;
  if (tx.description && !hasTechnicalIds(tx.description)) return tx.description;
  return `Retenue enfant à charge : ${source} → ${target}${period}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// ATOMS
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge = ({ status, config = STATUS_CONFIG }) => {
  const c = config[status] || { label: status, color: 'bg-gray-100 text-gray-600', icon: CircleDot };
  const I = c.icon;
  return (
    <span className={`${BADGE} ${c.color}`}>
      <I size={11} />{c.label}
    </span>
  );
};



const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-14 px-4">
    <div className="w-14 h-14 rounded-lg bg-gray-50 flex items-center justify-center mb-3 border border-gray-100">
      <Icon size={24} className="text-gray-300" />
    </div>
    <p className="text-sm font-semibold text-gray-700 mb-1">{title}</p>
    <p className="text-xs text-gray-400 text-center max-w-xs">{description}</p>
  </div>
);

const Surface = ({ children, className = '' }) => (
  <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>{children}</div>
);

const MetricTile = ({ label, value, icon: Icon, tone = 'primary' }) => {
  const tones = {
    primary: 'bg-[#246990]/10 text-[#246990]',
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-teal-50 text-teal-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    gray: 'bg-gray-100 text-gray-700'
  };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone] || tones.primary}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 truncate">{label}</p>
        <p className="text-base font-semibold text-gray-900 truncate">{value}</p>
      </div>
    </div>
  );
};

const SearchBox = ({ value, onChange, placeholder }) => (
  <div className="relative min-w-[220px]">
    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
    />
  </div>
);

const FilterButton = ({ active, children, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`h-9 px-3 rounded-lg text-xs font-semibold transition-all border ${active
      ? 'bg-[#246990] text-white border-[#246990] shadow-sm'
      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
      }`}
  >
    {children}
    {typeof count === 'number' && count > 0 && (
      <span className={`ml-1.5 ${active ? 'text-white/75' : 'text-gray-400'}`}>{count}</span>
    )}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// STAT CARDS — compactes, alignées avec les section cards
// ─────────────────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, detail, icon: Icon, loading = false, color = 'from-[#246990] to-[#3084b5]' }) => (
  <div className="overflow-hidden rounded-lg border-0 shadow-md">
    <div className={`bg-gradient-to-r ${color} p-4 text-white`}>
      <div className="mb-2 flex items-center gap-1 text-sm font-medium">
        <Icon size={16} className="text-white" />
        <span className="text-xs leading-tight">{title}</span>
      </div>
      <p className="hidden text-xs text-white/80 md:block">{detail || 'Stats actuelles'}</p>
      <div className="mt-2 flex flex-col items-center justify-center">
          {loading ? (
            <div className="flex items-center gap-2">
              <RefreshCw size={18} className="animate-spin text-white" />
              <span className="text-2xl font-bold text-white/80">...</span>
            </div>
          ) : (
            <span className="text-2xl font-bold">{value}</span>
          )}
        </div>
      </div>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SECTION CARDS — style ParametrageApp
// ─────────────────────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'transfers', name: 'Transferts',
    description: 'Mouvements inter-sites du personnel et des élèves',
    icon: ArrowRightLeft, color: 'blue', count: 'Employés & Élèves',
    subtabs: [{ id: 'staff', label: 'Personnel', icon: UserCheck }, { id: 'eleves', label: 'Élèves', icon: GraduationCap }],
  },
  {
    id: 'children', name: 'Enfants à Charge',
    description: 'Retenues salariales → scolarité sur site distant',
    icon: Link2, color: 'green', count: 'Liaisons & Transactions',
    subtabs: [{ id: 'links', label: 'Liaisons', icon: Link2 }, { id: 'tx', label: 'Transactions', icon: CreditCard }],
  },
  {
    id: 'familles', name: 'Familles Inter-Sites',
    description: 'Familles réparties sur plusieurs établissements',
    icon: Users, color: 'purple', count: 'Gestion familiale',
  },
  {
    id: 'coherence', name: 'Cohérence & reprises',
    description: 'Écarts entre les sites et propagations restées en attente',
    icon: ShieldAlert, color: 'orange', count: 'Contrôle',
  },
  {
    id: 'audit', name: "Journal d'Audit",
    description: 'Historique complet des opérations inter-sites',
    icon: History, color: 'orange', count: 'Traçabilité',
  },
];

const getIconColor = c => ({ blue: 'text-blue-600 bg-blue-100', green: 'text-teal-600 bg-teal-100', orange: 'text-orange-600 bg-orange-100', purple: 'text-purple-600 bg-purple-100' }[c] || 'text-gray-600 bg-gray-100');
const getBtnColor = c => ({ blue: 'bg-blue-50 text-blue-700 hover:bg-blue-100', green: 'bg-teal-50 text-teal-700 hover:bg-teal-100', orange: 'bg-orange-50 text-orange-700 hover:bg-orange-100', purple: 'bg-purple-50 text-purple-700 hover:bg-purple-100' }[c] || 'bg-gray-50 text-gray-700');
const getTextColor = c => ({ blue: 'text-blue-700', green: 'text-teal-700', orange: 'text-amber-700', purple: 'text-violet-700' }[c] || 'text-gray-700');

const SectionCard = ({ section, onClick, extraCount }) => {
  const Icon = section.icon;
  const hasBadge = Number(extraCount || 0) > 0;
  return (
    <div
      className="bg-white border border-gray-200 shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:border-[#246990]/35 rounded-lg group"
      onClick={onClick}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getIconColor(section.color)}`}>
            <Icon size={20} />
          </div>
          {hasBadge && (
            <span className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-[#246990] text-white text-[10px] font-semibold">
              {extraCount}
            </span>
          )}
        </div>

        <h3 className="font-semibold text-sm text-gray-900 mb-1">{section.name}</h3>
        <p className="text-xs text-gray-600 mb-3">{section.description}</p>

        <div className="space-y-2 text-xs mb-4">
          <div className="flex justify-between">
            <span className="text-gray-500">Contenu</span>
            <span className={`font-semibold ${getTextColor(section.color)}`}>{section.count}</span>
          </div>

          {section.subtabs && (
            <div className="flex gap-1 mt-2 flex-wrap">
              {section.subtabs.map(t => (
                <span key={t.id} className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-100 text-[10px] font-medium text-gray-500">
                  <t.icon size={9} />{t.label}
                </span>
              ))}
            </div>
          )}
        </div>

        <button className={`w-full py-2 rounded-lg text-xs font-semibold transition-all ${getBtnColor(section.color)} flex items-center justify-center gap-2`}>
          Ouvrir <ChevronRight size={13} className="transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// OVERVIEW — carte du groupe + activité récente
// ─────────────────────────────────────────────────────────────────────────────
const OverviewPanel = ({ group, siblings, currentSite, transfers, eleveTransfers = [], transactions }) => {
  const latestTransfers = sortRecent([
    ...transfers.map(t => ({ ...t, _kind: 'staff' })),
    ...eleveTransfers.map(t => ({ ...t, _kind: 'eleve', status: t.statut }))
  ]).slice(0, 4);
  const latestTransactions = sortRecent(transactions).slice(0, 4);

  return (
    <div className="space-y-4">
      <Surface className="overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-50 flex items-center justify-between bg-gray-50/60">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#246990]/10 flex items-center justify-center">
              <Globe size={16} className="text-[#246990]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{group?.nom || 'Groupe Inter-Sites'}</h3>
              <p className="text-[11px] text-gray-400">Vos établissements inter-site</p>
            </div>
          </div>

        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative p-3.5 rounded-lg border-2 border-[#246990]/30 bg-[#246990]/5">
              <div className="absolute -top-2.5 left-3 px-2 bg-white text-[11px] font-semibold text-[#246990] uppercase tracking-wide">Site actuel</div>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-8 h-8 rounded-lg bg-[#246990] flex items-center justify-center flex-shrink-0">
                  <Building2 size={14} className="text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-900 truncate">{currentSite || 'Ce site'}</p>

                  <p className="text-[11px] text-gray-400">Connecté au Hub</p>
                </div>
              </div>
              <div className="mt-2.5 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
                <span className="text-[11px] text-teal-600 font-semibold">Connecté</span>
              </div>
            </div>

            {siblings.slice(0, 4).map((site, idx) => (
              <div key={site.id || idx} className="p-3.5 rounded-lg border border-gray-100 hover:border-[#246990]/20 hover:shadow-sm transition-all">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <Building size={14} className="text-gray-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{site.nom || site.site_name}</p>
                    {/*<p className="text-[11px] text-gray-400 truncate">{site.tenant_db || site.database}</p> */}
                  </div>
                </div>
                <div className="mt-2.5 flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                  <span className="text-[11px] text-gray-400 font-medium">Connecté</span>
                </div>
              </div>
            ))}

            {siblings.length === 0 && (
              <div className="col-span-2 flex items-center justify-center p-6 rounded-lg border border-dashed border-gray-200">
                <p className="text-xs text-gray-400">Aucun site frère configuré</p>
              </div>
            )}
          </div>
        </div>
      </Surface>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[
          {
            title: 'Derniers transferts', icon: ArrowRightLeft, total: transfers.length + eleveTransfers.length,
            items: latestTransfers,
            render: (t, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50/50 transition-colors border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${t.status === 'executed' ? 'bg-teal-50' : t.status === 'rejected' ? 'bg-red-50' : 'bg-amber-50'}`}>
                    {t._kind === 'eleve' ? <GraduationCap size={13} className={t.status === 'executed' ? 'text-teal-500' : t.status === 'rejected' ? 'text-red-500' : 'text-amber-500'} /> : t.status === 'executed' ? <UserCheck size={13} className="text-teal-500" /> : t.status === 'rejected' ? <UserX size={13} className="text-red-500" /> : <Clock size={13} className="text-amber-500" />}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">{t._kind === 'eleve' ? `${t.eleve_nom || ''} ${t.eleve_prenom || ''}`.trim() : (t.entity_name || t.nom_employe || 'Employé')}</p>
                    <p className="text-[11px] text-gray-400">{t.from_site || t.site_source} → {t.to_site || t.site_cible}</p>
                  </div>
                </div>
                <StatusBadge status={t.status} />
              </div>
            ),
          },
          {
            title: 'Dernières transactions', icon: CreditCard, total: transactions.length,
            items: latestTransactions,
            render: (tx, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50/50 transition-colors border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${tx.type === 'credit' ? 'bg-teal-50' : 'bg-orange-50'}`}>
                    {tx.type === 'credit' ? <ArrowDownLeft size={13} className="text-teal-500" /> : <ArrowUpRight size={13} className="text-orange-500" />}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">{fmt(tx.montant)}</p>
                    <p className="text-[11px] text-gray-400">{tx.source_site || tx.site_source || tx.source_site_subdomain} → {tx.target_site || tx.site_cible || tx.target_site_subdomain}</p>
                  </div>
                </div>
                <StatusBadge status={tx.status} config={TX_STATUS} />
              </div>
            ),
          },
        ].map(col => (
          <Surface key={col.title}>
            <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/60 flex items-center justify-between">
              <h4 className="text-xs font-semibold text-gray-800 flex items-center gap-2">
                <col.icon size={13} className="text-[#246990]" />{col.title}
              </h4>
              <span className="text-[11px] text-gray-400">{col.total} total</span>
            </div>
            {col.items.length === 0
              ? <div className="px-4 py-6 text-center text-xs text-gray-400">Aucune entrée</div>
              : col.items.map((item, i) => col.render(item, i))
            }
          </Surface>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DRILL-IN HEADER
// ─────────────────────────────────────────────────────────────────────────────
const DrillInHeader = ({ section, onBack }) => {
  const Icon = section.icon;
  return (
    <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
      <button
        onClick={onBack}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs font-semibold text-gray-600 transition-colors"
      >
        <ChevronLeft size={14} />Retour
      </button>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getIconColor(section.color)}`}>
        <Icon size={16} />
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: { blue: '#3b82f6', green: '#10b981', orange: '#f59e0b', purple: '#8b5cf6' }[section.color] }}>{section.count}</p>
        <h2 className="text-sm font-semibold text-gray-900">{section.name}</h2>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-TAB BAR
// ─────────────────────────────────────────────────────────────────────────────
// Barre d'onglets du module, sur la primitive Radix partagee par le reste de
// l'application.
//
// C'etait la seule barre d'onglets ecrite a la main du projet — 27 autres
// fichiers utilisent deja `ui/tabs`. La version maison n'etait qu'une rangee de
// boutons : pas de navigation aux fleches, pas de `role="tablist"`, pas de
// `aria-selected`, pas d'anneau de focus. Un lecteur d'ecran annoncait
// « bouton » la ou il fallait entendre « onglet 1 sur 2 ».
//
// L'onglet actif prend le fond #246990 et le texte blanc, comme en comptabilite
// et dans la majorite des modules. L'ancien contraste inverse — fond blanc,
// texte bleu — singularisait le module sans rien apporter.
const SubTabBar = ({ tabs, active, onChange, children }) => (
  <Tabs value={active} onValueChange={onChange}>
    <TabsList className="h-10 gap-1 mb-5 p-1 bg-gray-100 rounded-lg w-fit max-w-full overflow-x-auto">
      {tabs.map(t => (
        <TabsTrigger
          key={t.id}
          value={t.id}
          className="flex items-center gap-2 px-4 h-8 rounded-md text-xs font-semibold whitespace-nowrap text-gray-500 hover:text-gray-700 data-[state=active]:bg-[#246990] data-[state=active]:text-white data-[state=active]:shadow-sm"
        >
          <t.icon size={13} />{t.label}
          {typeof t.count === 'number' && (
            <span className="opacity-70">{t.count}</span>
          )}
        </TabsTrigger>
      ))}
    </TabsList>
    {children}
  </Tabs>
);

// ─────────────────────────────────────────────────────────────────────────────
// SECTION WRAPPERS
// ─────────────────────────────────────────────────────────────────────────────
const TransfersSectionContent = ({ transfers, siblings, currentSite, onRefresh }) => {
  const [sub, setSub] = useState('staff');
  return (
    <div>
      <SubTabBar
        tabs={[
          { id: 'staff', label: 'Personnel & Enseignants', icon: UserCheck, count: transfers.length },
          { id: 'eleves', label: 'Élèves', icon: GraduationCap }
        ]}
        active={sub}
        onChange={setSub}
      >
        <TabsContent value="staff" className="mt-0">
          <TransfersTab transfers={transfers} siblings={siblings} currentSite={currentSite} onRefresh={onRefresh} />
        </TabsContent>
        <TabsContent value="eleves" className="mt-0">
          <EleveTransfersTab siblings={siblings} currentSite={currentSite} onRefresh={onRefresh} />
        </TabsContent>
      </SubTabBar>
    </div>
  );
};

const ChildrenSectionContent = ({ childLinks, transactions, siblings, currentSite, onRefresh }) => {
  const [sub, setSub] = useState('links');
  return (
    <div>
      <SubTabBar
        tabs={[
          { id: 'links', label: 'Liaisons', icon: Link2, count: childLinks.length },
          { id: 'tx', label: 'Transactions', icon: CreditCard, count: transactions.length }
        ]}
        active={sub}
        onChange={setSub}
      >
        <TabsContent value="links" className="mt-0">
          <ChildLinksTab childLinks={childLinks} siblings={siblings} currentSite={currentSite} onRefresh={onRefresh} />
        </TabsContent>
        <TabsContent value="tx" className="mt-0">
          <TransactionsTab transactions={transactions} onRefresh={onRefresh} />
        </TabsContent>
      </SubTabBar>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TABS MÉTIER
// ─────────────────────────────────────────────────────────────────────────────
const TransfersTab = ({ transfers, siblings, currentSite, onRefresh }) => {
  const [filter, setFilter] = useState('all');
  const [direction, setDirection] = useState('all');
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [actionLoading, setAL] = useState(null);
  const [executingTransfer, setExecutingTransfer] = useState(null);
  const [toast, setToast] = useState(null);
  const isOutgoing = t => (t.from_site === currentSite || t.site_source === currentSite || t.direction === 'outgoing');
  const filtered = transfers.filter(t => {
    if (filter !== 'all' && t.status !== filter) return false;
    if (direction === 'outgoing' && !isOutgoing(t)) return false;
    if (direction === 'incoming' && isOutgoing(t)) return false;
    if (!query.trim()) return true;
    const haystack = normalizeText([
      t.entity_name, t.nom_employe, t.from_site, t.site_source, t.to_site, t.site_cible, t.motif, t.reference
    ].join(' '));
    return haystack.includes(normalizeText(query));
  });
  const counts = {
    all: transfers.length,
    pending: transfers.filter(t => t.status === 'pending').length,
    approved_source: transfers.filter(t => t.status === 'approved_source').length,
    approved_both: transfers.filter(t => t.status === 'approved_both').length,
    executed: transfers.filter(t => t.status === 'executed').length,
    rejected: transfers.filter(t => t.status === 'rejected').length,
    outgoing: transfers.filter(isOutgoing).length,
    incoming: transfers.filter(t => !isOutgoing(t)).length
  };
  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedTransfers,
    pageSize
  } = usePaginatedList(filtered, PAGE_SIZE_CARDS, `${filter}|${direction}|${query}`);
  const notify = (message, type = 'success') => setToast({ message, type });

  const handleAction = async (transferOrId, action, reason = null) => {
    const transfer = typeof transferOrId === 'object'
      ? transferOrId
      : transfers.find(item => item.id === transferOrId);
    const id = transfer?.id || transferOrId;
    setAL(id);
    if (action === 'execute') setExecutingTransfer(transfer || null);
    try {
      let r;
      if (action === 'approve-departure') r = await api.intersite.approveDeparture(id);
      else if (action === 'approve-arrival') r = await api.intersite.approveArrival(id);
      else if (action === 'execute') r = await api.intersite.executeTransfer(id);
      else if (action === 'reject') {
        r = await api.intersite.rejectTransfer(id, reason);
      }
      if (r?.success) {
        notify('Action effectuée avec succès.');
        if (action === 'reject') setRejectTarget(null);
        onRefresh();
      } else {
        notify(getApiError(r, 'Action impossible'), 'error');
      }
    } catch (e) { notify(e.message || 'Erreur lors de l action', 'error'); }
    setAL(null);
    if (action === 'execute') setExecutingTransfer(null);
  };


  // Consulter n'est pas agir : le dossier prend la place de la liste au lieu de
  // s'empiler par-dessus. Le retour ramène la liste dans l'état où elle était.
  if (selected) {
    return (
      <TransferDetailPage
        transfer={selected}
        onClose={() => setSelected(null)}
        currentSite={currentSite}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MetricTile label="Total demandes" value={counts.all} icon={ArrowRightLeft} />
        <MetricTile label="À valider" value={counts.pending + counts.approved_source} icon={Clock} tone="amber" />
        <MetricTile label="Prêtes" value={counts.approved_both} icon={ShieldCheck} tone="green" />
        <MetricTile label="Exécutées" value={counts.executed} icon={CheckCircle2} tone="blue" />
      </div>

      <Surface className="p-3">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {['all', 'pending', 'approved_source', 'approved_both', 'executed', 'rejected'].map(s => (
              <FilterButton key={s} active={filter === s} onClick={() => setFilter(s)} count={s === 'all' ? counts.all : counts[s]}>
                {s === 'all' ? 'Tous' : STATUS_CONFIG[s]?.label || s}
              </FilterButton>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex p-1 bg-gray-100 rounded-lg">
              {[{ k: 'all', l: 'Tous' }, { k: 'outgoing', l: 'Sortants' }, { k: 'incoming', l: 'Entrants' }].map(d => (
                <button
                  key={d.k}
                  type="button"
                  onClick={() => setDirection(d.k)}
                  className={`h-7 px-3 rounded-md text-[11px] font-semibold ${direction === d.k ? 'bg-white text-[#246990] shadow-sm' : 'text-gray-500'}`}
                >
                  {d.l}{d.k !== 'all' && <span className="ml-1 opacity-60">{counts[d.k]}</span>}
                </button>
              ))}
            </div>
            <SearchBox value={query} onChange={setQuery} placeholder="Rechercher un transfert..." />
            <button onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 h-9 bg-[#246990] text-white text-xs font-semibold rounded-lg hover:bg-[#1f5a7a] transition-colors shadow-sm">
              <Plus size={13} />Nouveau transfert
            </button>
          </div>
        </div>
      </Surface>

      <Surface className="overflow-hidden relative">
        {filtered.length === 0
          ? <EmptyState icon={ArrowRightLeft} title="Aucun transfert" description="Les demandes de transfert apparaîtront ici." />
          : (
            <div className="overflow-x-auto">
              <div className="min-w-[980px]">
                <div className="grid grid-cols-[minmax(250px,1.5fr)_minmax(260px,1.25fr)_110px_135px_145px_130px] gap-4 px-5 py-3 bg-gray-50/70 border-b border-gray-100">
                  {['Dossier', 'Trajet', 'Direction', 'Date', 'Statut', 'Actions'].map((h, idx) => (
                    <div key={h} className={`text-[11px] font-semibold text-gray-500 uppercase tracking-wide ${idx >= 4 ? 'text-center' : 'text-left'}`}>
                      {h}
                    </div>
                  ))}
                </div>
                <div className="divide-y divide-gray-50">
              {paginatedTransfers.map((t, i) => {
                const out = isOutgoing(t);
                const source = t.from_site || t.site_source;
                const target = t.to_site || t.site_cible;
                return (
                  <div key={t.id || i} className="grid grid-cols-[minmax(250px,1.5fr)_minmax(260px,1.25fr)_110px_135px_145px_130px] gap-4 px-5 py-3.5 items-center hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${out ? 'bg-orange-50' : 'bg-blue-50'}`}>
                        {out ? <ArrowUpRight size={16} className="text-orange-500" /> : <ArrowDownLeft size={16} className="text-blue-500" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{t.entity_name || t.nom_employe || 'Employé'}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5 truncate">{transferTypeLabel(t.entity_type)} · {t.reference || `TRF-${t.id}`}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 w-fit">
                      <SiteTag name={source} isCurrent={!out} />
                      <ChevronRight size={11} className="text-gray-300 flex-shrink-0" />
                      <SiteTag name={target} isCurrent={out} />
                    </div>
                    <div>
                      <span className={`inline-flex items-center justify-center w-full px-2 py-1 rounded-md text-[10px] font-semibold ${out ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>
                        {out ? 'Sortant' : 'Entrant'}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500">{fmtDT(t.created_at)}</p>
                    <div className="flex justify-center">
                      <StatusBadge status={t.status} />
                    </div>
                    <div className="flex items-center justify-center gap-1">
                        {t.status === 'pending' && out && <button title="Valider le départ" onClick={() => handleAction(t.id, 'approve-departure')} disabled={actionLoading === t.id} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">{actionLoading === t.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={13} />}</button>}
                        {t.status === 'approved_source' && !out && <button title="Valider l'arrivée" onClick={() => handleAction(t.id, 'approve-arrival')} disabled={actionLoading === t.id} className="p-1.5 rounded-lg bg-teal-50 text-teal-600 hover:bg-teal-100 transition-colors">{actionLoading === t.id ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={13} />}</button>}
                        {t.status === 'approved_both' && <button title="Exécuter le transfert" onClick={() => handleAction(t, 'execute')} disabled={actionLoading === t.id} className="p-1.5 rounded-lg bg-[#246990]/10 text-[#246990] hover:bg-[#246990]/20 transition-colors">{actionLoading === t.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />}</button>}
                        {(t.status === 'pending' || t.status === 'approved_source') && <button title="Rejeter" onClick={() => setRejectTarget(t)} className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"><X size={13} /></button>}
                        <button title="Voir les détails" onClick={() => setSelected(t)} className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:bg-gray-100 transition-colors"><Eye size={13} /></button>
                    </div>
                  </div>
                );
              })}
                </div>
              </div>
            </div>
          )
        }
      </Surface>
      <PaginationBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalItems={filtered.length}
        pageSize={pageSize}
        label="transfert"
      />
      <>
        {showCreate && <CreateTransferModal siblings={siblings} currentSite={currentSite} onClose={() => setShowCreate(false)} onCreated={(message) => { setShowCreate(false); notify(message || 'Demande envoyée avec succès.'); onRefresh(); }} />}
        {rejectTarget && (
          <RejectActionModal
            title="Rejeter le transfert"
            subtitle={rejectTarget.entity_name || rejectTarget.nom_employe || 'Demande de transfert'}
            loading={actionLoading === rejectTarget.id}
            onClose={() => setRejectTarget(null)}
            onConfirm={(reason) => handleAction(rejectTarget.id, 'reject', reason)}
          />
        )}
        {executingTransfer && (
          <TransferExecutionOverlay
            title="Exécution du transfert"
            subtitle="Le dossier personnel est en cours de bascule vers le site cible."
            name={executingTransfer.entity_name || executingTransfer.nom_employe || 'Personnel inter-site'}
            source={executingTransfer.from_site || executingTransfer.site_source}
            target={executingTransfer.to_site || executingTransfer.site_cible}
          />
        )}
      </>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

const ChildLinksTab = ({ childLinks, siblings, currentSite, onRefresh }) => {
  // Clôture définitive d'une liaison enfant à charge.
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [analyse, setAnalyse] = useState(null);
  const [termId, setTermId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [showCreate, setShow] = useState(false);
  const [reacId, setReacId] = useState(null);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const filtered = childLinks.filter(l => {
    if (filter !== 'all' && l.statut !== filter) return false;
    if (!query.trim()) return true;
    const haystack = normalizeText([
      l.parent_name, l.nom_employe, l.child_name, l.nom_eleve, l.child_classe, l.classe, l.parent_site, l.child_site, l.site_source, l.site_cible
    ].join(' '));
    return haystack.includes(normalizeText(query));
  });
  const counts = { all: childLinks.length, active: childLinks.filter(l => l.statut === 'active').length, suspended: childLinks.filter(l => l.statut === 'suspended').length, terminated: childLinks.filter(l => l.statut === 'terminated').length };
  const restoreCount = childLinks.filter(l => l.statut === 'suspended' && l.restoration_detected).length;
  const monthlyTotal = childLinks.filter(l => l.statut === 'active').reduce((sum, l) => sum + Number(l.montant_mensuel || 0), 0);
  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedLinks,
    pageSize
  } = usePaginatedList(filtered, PAGE_SIZE_TABLE, `${filter}|${query}`);

  // Terminer une liaison.
  //
  // On pouvait la créer et la réactiver, jamais la fermer : une liaison dont
  // l'élève a disparu restait « active » indéfiniment, à retenir un montant
  // chaque mois pour un destinataire qui n'existe plus. Le contrôle de
  // cohérence savait le dire ; aucun écran ne permettait d'y remédier.
  // On n'ouvre pas la confirmation les mains vides : on va d'abord demander au
  // serveur ce que la clôture changerait réellement. Fermer une liaison dont
  // l'élève a disparu ne prive personne ; fermer une liaison vivante interrompt
  // le paiement d'une scolarité. Ce n'est pas le même geste, et la personne qui
  // clique doit savoir lequel des deux elle s'apprête à faire.
  const ouvrirCloture = async (lk) => {
    setTerminateTarget(lk);
    setAnalyse({ chargement: true });
    try {
      const r = await api.intersite.analyserClotureChildLink(lk.id);
      setAnalyse(r?.success ? { donnees: r.data } : { erreur: getApiError(r, 'Analyse impossible') });
    } catch (e) {
      setAnalyse({ erreur: e.message || 'Analyse impossible' });
    }
  };

  const handleTerminate = async (id, raison) => {
    setTermId(id);
    try {
      const r = await api.intersite.terminateChildLink(id, raison);
      if (r?.success) {
        setTerminateTarget(null);
        setToast({ type: 'success', message: r.message || 'Liaison terminée.' });
        onRefresh();
      } else {
        setToast({ type: 'error', message: getApiError(r, 'Clôture impossible') });
      }
    }
    catch (e) { setToast({ type: 'error', message: e.message || 'Erreur lors de la clôture' }); }
    setTermId(null);
  };

  const handleReactivate = async (id) => {
    setReacId(id);
    try {
      const r = await api.intersite.reactivateChildLink(id);
      if (r?.success) {
        setReactivateTarget(null);
        setToast({ type: 'success', message: 'Liaison réactivée avec succès.' });
        onRefresh();
      } else {
        setToast({ type: 'error', message: getApiError(r, 'Réactivation impossible') });
      }
    }
    catch (e) { setToast({ type: 'error', message: e.message || 'Erreur lors de la réactivation' }); }
    setReacId(null);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MetricTile label="Liaisons totales" value={counts.all} icon={Link2} />
        <MetricTile label="Actives" value={counts.active} icon={CheckCircle2} tone="green" />
        <MetricTile label="À réactiver" value={restoreCount} icon={UserPlus} tone="amber" />
        <MetricTile label="Retenue mensuelle" value={fmt(monthlyTotal)} icon={Banknote} tone="blue" />
      </div>

      {restoreCount > 0 && (
        <div
          className="flex items-start gap-3 px-4 py-3 bg-teal-50 border border-teal-200 rounded-lg">
          <UserPlus size={15} className="text-teal-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-teal-800">{restoreCount} élève(s) restauré(s) — réactivation requise</p>
            <p className="text-[11px] text-teal-600 mt-0.5">Des élèves précédemment radiés sont revenus dans l'effectif.</p>
          </div>
        </div>
      )}
      <Surface className="p-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {[{ k: 'all', l: 'Toutes' }, { k: 'active', l: 'Actives' }, { k: 'suspended', l: 'Suspendues' }, { k: 'terminated', l: 'Terminées' }].map(f => (
              <FilterButton key={f.k} active={filter === f.k} onClick={() => setFilter(f.k)} count={counts[f.k]}>{f.l}</FilterButton>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SearchBox value={query} onChange={setQuery} placeholder="Rechercher parent, enfant, site..." />
            <button onClick={() => setShow(true)}
              className="flex items-center gap-2 px-4 h-9 bg-[#246990] text-white text-xs font-semibold rounded-lg hover:bg-[#1f5a7a] transition-colors shadow-sm">
              <Plus size={13} />Nouvelle liaison
            </button>
          </div>
        </div>
      </Surface>
      <Surface className="overflow-hidden relative overflow-x-auto">
        {filtered.length === 0
          ? <EmptyState icon={Link2} title="Aucune liaison" description="Créez une liaison pour automatiser les retenues salariales cross-tenant." />
          : (
            <table className="w-full min-w-[920px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  {['Parent', 'Site parent', '', 'Enfant', 'Site enfant', 'Mensuel', 'Statut', 'Actions'].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginatedLinks.map((lk, i) => {
                  const susp = lk.statut === 'suspended', term = lk.statut === 'terminated';
                  const restore = susp && !!lk.restoration_detected;
                  return (
                    <tr key={lk.id || i} className={`transition-colors ${restore ? 'bg-teal-50/30' : susp ? 'bg-amber-50/20' : term ? 'opacity-55' : 'hover:bg-gray-50/30'}`}>
                      <td className="px-4 py-3"><p className="text-xs font-semibold text-gray-800">{lk.parent_name || lk.nom_employe}</p><p className="text-[11px] text-gray-400">{lk.parent_role || 'Employé'}</p></td>
                      <td className="px-4 py-3"><SiteTag name={lk.parent_site || lk.parent_site_subdomain || lk.site_source} isCurrent={(lk.parent_site || lk.parent_site_subdomain || lk.site_source) === currentSite} /></td>
                      <td className="px-2 py-3 text-center"><ChevronRight size={12} className="text-[#246990]" /></td>
                      <td className="px-4 py-3"><p className="text-xs font-semibold text-gray-800">{lk.child_name || lk.nom_eleve}</p><p className="text-[11px] text-gray-400">{lk.child_classe || lk.classe || ''}</p></td>
                      <td className="px-4 py-3"><SiteTag name={lk.child_site || lk.child_site_subdomain || lk.site_cible} isCurrent={(lk.child_site || lk.child_site_subdomain || lk.site_cible) === currentSite} /></td>
                      <td className="px-4 py-3 text-right"><span className={`text-xs font-semibold ${term ? 'text-gray-400 line-through' : susp ? 'text-amber-600' : 'text-gray-900'}`}>{fmt(lk.montant_mensuel)}</span></td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={restore ? 'restore' : (term ? 'terminated' : (susp ? 'suspended' : 'active'))}
                          config={LIAISON_STATUS}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {restore && (lk.parent_site || lk.parent_site_subdomain) === currentSite && (
                          <button onClick={() => setReactivateTarget(lk)} disabled={reacId === lk.id}
                            className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-500 text-white text-[11px] font-semibold rounded-lg hover:bg-teal-600 disabled:opacity-50 transition-colors">
                            {/* `ArchiveRestore` : `RefreshCw` est le glyphe « Actualiser » de
                                l'application, il se lisait comme un chargement sur un bouton
                                qui remet une liaison en service. Les autres `RefreshCw` du
                                fichier restent : ce sont de vrais rafraichissements. */}
                            {reacId === lk.id ? <Loader2 size={14} className="animate-spin" /> : <ArchiveRestore size={10} />}Réactiver
                          </button>
                        )}
                        {susp && !restore && <span className="text-[11px] text-amber-400 italic">Élève radié</span>}
                        {term && <span className="text-[11px] text-gray-300 italic">Archivée</span>}
                        {!term && [
                          lk.parent_site, lk.parent_site_subdomain,
                          lk.child_site, lk.child_site_subdomain
                        ].includes(currentSite) && (
                          <button onClick={() => ouvrirCloture(lk)} disabled={termId === lk.id}
                            className="mt-1 flex items-center gap-1.5 px-2.5 py-1 border border-gray-200 text-gray-600 text-[11px] font-semibold rounded-lg hover:border-red-300 hover:text-red-600 disabled:opacity-50 transition-colors">
                            {termId === lk.id ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={10} />}Terminer
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        }
      </Surface>
      <PaginationBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalItems={filtered.length}
        pageSize={pageSize}
        label="liaison"
      />
      <>
        {showCreate && <CreateChildLinkModal siblings={siblings} currentSite={currentSite} onClose={() => setShow(false)} onCreated={() => { setShow(false); setToast({ type: 'success', message: 'Liaison créée avec succès.' }); onRefresh(); }} />}
        {terminateTarget && (
          <ClotureLiaisonModal
            liaison={terminateTarget}
            analyse={analyse}
            loading={termId === terminateTarget.id}
            onClose={() => { setTerminateTarget(null); setAnalyse(null); }}
            onConfirm={(raison) => handleTerminate(terminateTarget.id, raison)}
          />
        )}
        {reactivateTarget && (
          <ConfirmActionModal
            title="Réactiver la liaison"
            subtitle={reactivateTarget.child_name || reactivateTarget.nom_eleve || 'Enfant à charge'}
            description="La retenue salariale inter-site reprendra pour cette liaison."
            confirmLabel="Réactiver"
            loading={reacId === reactivateTarget.id}
            icon={RefreshCw}
            onClose={() => setReactivateTarget(null)}
            onConfirm={() => handleReactivate(reactivateTarget.id)}
          />
        )}
      </>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

const TransactionsTab = ({ transactions, onRefresh }) => {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [toast, setToast] = useState(null);
  const filtered = transactions.filter(t => {
    if (filter !== 'all' && t.status !== filter) return false;
    if (!query.trim()) return true;
    const haystack = normalizeText([
      transactionReadableDescription(t), t.description_lisible, t.reference,
      t.nom_employe, t.parent_name, t.nom_eleve, t.child_name,
      t.source_site, t.target_site, t.site_source, t.site_cible, t.status
    ].join(' '));
    return haystack.includes(normalizeText(query));
  });
  const counts = {
    all: transactions.length,
    completed: transactions.filter(t => t.status === 'completed').length,
    pending: transactions.filter(t => t.status === 'pending').length,
    failed: transactions.filter(t => t.status === 'failed').length
  };
  const totalCompleted = transactions.filter(t => t.status === 'completed').reduce((sum, t) => sum + Number(t.montant || 0), 0);
  const totalPending = transactions.filter(t => t.status === 'pending').reduce((sum, t) => sum + Number(t.montant || 0), 0);
  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedTransactions,
    pageSize
  } = usePaginatedList(filtered, PAGE_SIZE_TABLE, `${filter}|${query}`);
  const handleApply = async id => {
    try {
      const r = await api.intersite.applyCreditManually(id);
      if (r?.success) {
        setToast({ type: 'success', message: 'Crédit appliqué avec succès.' });
        onRefresh();
      } else {
        setToast({ type: 'error', message: getApiError(r, 'Application impossible') });
      }
    } catch (e) { setToast({ type: 'error', message: e.message || 'Erreur lors de l’application' }); }
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MetricTile label="Transactions" value={counts.all} icon={CreditCard} />
        <MetricTile label="Complétées" value={counts.completed} icon={CheckCircle2} tone="green" />
        <MetricTile label="En attente" value={fmt(totalPending)} icon={Clock} tone="amber" />
        <MetricTile label="Volume crédité" value={fmt(totalCompleted)} icon={Banknote} tone="blue" />
      </div>

      <Surface className="p-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {['all', 'completed', 'pending', 'failed'].map(s => (
              <FilterButton key={s} active={filter === s} onClick={() => setFilter(s)} count={counts[s]}>
                {s === 'all' ? 'Toutes' : TX_STATUS[s]?.label || s}
              </FilterButton>
            ))}
          </div>
          <SearchBox value={query} onChange={setQuery} placeholder="Rechercher une transaction..." />
        </div>
      </Surface>

      <Surface className="overflow-hidden relative overflow-x-auto">
        {filtered.length === 0
          ? <EmptyState icon={CreditCard} title="Aucune transaction" description="Les transactions comptables inter-sites apparaîtront ici." />
          : (
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  {['Date', 'Description', 'Trajet', 'Montant', 'Statut', ''].map((h, i) => (
                    <th key={i} className={`px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide ${i === 3 ? 'text-right' : i >= 4 ? 'text-center' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginatedTransactions.map((tx, i) => (
                  <tr key={tx.id || i} className="hover:bg-gray-50/30 transition-colors">
                    <td className="px-4 py-3 text-[11px] text-gray-500">{fmtDT(tx.created_at)}</td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-semibold text-gray-800 leading-relaxed">{transactionReadableDescription(tx)}</p>
                    </td>
                    <td className="px-4 py-3"><div className="flex items-center gap-1.5"><SiteTag name={tx.source_site || tx.site_source} /><ChevronRight size={9} className="text-gray-300" /><SiteTag name={tx.target_site || tx.site_cible} /></div></td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{fmt(tx.montant)}</td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={tx.status} config={TX_STATUS} /></td>
                    <td className="px-4 py-3 text-center">
                      {(tx.status === 'pending' || tx.status === 'failed') && (
                        <button onClick={() => handleApply(tx.id)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${tx.status === 'failed' ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-[#246990]/10 text-[#246990] hover:bg-[#246990]/20'}`}>
                          <RefreshCw size={9} />{tx.status === 'failed' ? 'Réessayer' : 'Appliquer'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Surface>
      <PaginationBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalItems={filtered.length}
        pageSize={pageSize}
        label="transaction"
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

const parseAuditDetails = details => {
  if (!details) return {};
  if (typeof details === 'object') return details;
  try { return JSON.parse(details); } catch { return { texte: String(details) }; }
};

const AUDIT_DETAIL_LABELS = {
  action: 'Action',
  approved_by: 'Validé par',
  approved_source_by: 'Départ validé par',
  approved_target_by: 'Arrivée validée par',
  child: 'Enfant',
  classe_cible: 'Classe cible',
  classe_source: 'Classe source',
  description: 'Description',
  created_by: 'Créé par',
  created_by_nom: 'Créé par',
  eleve: 'Élève',
  eleve_id: 'ID élève',
  eleve_id_cible: 'ID élève cible',
  eleve_id_source: 'ID élève source',
  eleve_nom: 'Élève',
  enfant: 'Enfant',
  entity_name: 'Dossier',
  error: 'Erreur',
  executed_by: 'Exécuté par',
  famille: 'Famille',
  famille_locale_detachee: 'Famille locale détachée',
  famille_locale_id: 'ID famille locale',
  from: 'Site source',
  from_site: 'Site source',
  montant: 'Montant',
  motif: 'Motif',
  motif_rejet: 'Motif de rejet',
  new_id: 'Nouvel ID',
  old_id: 'Ancien ID',
  parent: 'Parent',
  reason: 'Motif',
  reference: 'Référence',
  rejected_by: 'Rejeté par',
  site_cible: 'Site cible',
  site_enfant: 'Site enfant',
  site_parent: 'Site parent',
  site_restant: 'Site restant',
  site_source: 'Site source',
  statut: 'Statut',
  status: 'Statut',
  step: 'Étape',
  tables_with_data: 'Tables archivées',
  to: 'Site cible',
  to_site: 'Site cible',
  total_rows: 'Lignes archivées'
};

const AUDIT_DETAIL_VALUES = {
  approved_both: 'Double validation',
  approved_source: 'Départ validé',
  completed: 'Complété',
  executed: 'Exécuté',
  failed: 'Échoué',
  pending: 'En attente',
  rejected: 'Rejeté',
  source_approval: 'Validation du départ',
  target_approval: "Validation de l'arrivée"
};

const AUDIT_DETAIL_HIDDEN_KEYS = new Set([
  'id',
  'eleve_id',
  'eleve_id_cible',
  'eleve_id_source',
  'famille_locale_id',
  'old_id',
  'new_id',
  'uuid',
  'transfer_uuid'
]);

const auditDetailLabel = key => AUDIT_DETAIL_LABELS[key] || String(key || '')
  .replace(/_/g, ' ')
  .replace(/\bapproved\b/gi, 'validé')
  .replace(/\bcreated\b/gi, 'créé')
  .replace(/\bexecuted\b/gi, 'exécuté')
  .replace(/\brejected\b/gi, 'rejeté')
  .replace(/\bsource\b/gi, 'source')
  .replace(/\btarget\b/gi, 'cible')
  .replace(/\bby\b/gi, 'par')
  .replace(/^\w/, c => c.toUpperCase());

const auditDetailValue = value => {
  if (typeof value === 'number') return value.toLocaleString('fr-FR');
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  return AUDIT_DETAIL_VALUES[value] || value;
};

const auditEntityLabel = type => ({
  transfer: 'Transfert',
  employe: 'Employé',
  enseignant: 'Enseignant',
  eleve_transfer: 'Transfert élève',
  child_link: 'Enfant à charge',
  transaction: 'Transaction',
  intersite_family: 'Famille inter-site',
  intersite_famille: 'Famille inter-site'
}[type] || (type ? String(type).replace(/_/g, ' ') : 'Journal Hub'));

const auditLabel = action => ({
  transfer_requested: 'Demande de transfert créée',
  transfer_created: 'Demande de transfert créée',
  transfer_approved_source: 'Départ validé',
  transfer_approved_departure: 'Départ validé',
  transfer_approved_target: 'Arrivée validée',
  transfer_approved_arrival: 'Arrivée validée',
  transfer_executed: 'Transfert exécuté',
  transfer_failed: 'Transfert échoué',
  transfer_rejected: 'Transfert rejeté',
  eleve_transfer_created: 'Demande élève créée',
  eleve_transfer_approved_source: 'Départ élève validé',
  eleve_transfer_approved_cible: 'Arrivée élève validée',
  eleve_transfer_executed: 'Transfert élève exécuté',
  eleve_transfer_rejected: 'Transfert élève rejeté',
  child_link_created: 'Liaison enfant créée',
  child_link_classe_changed: 'Classe enfant mise à jour',
  child_link_suspended: 'Liaison suspendue',
  child_link_restoration_detected: 'Restauration enfant détectée',
  child_link_reactivated: 'Liaison réactivée',
  child_link_terminated: 'Liaison terminée',
  child_link_terminated_by_transfer: 'Liaison terminée par transfert',
  child_link_fees_updated: 'Frais enfant mis à jour',
  credit_applied: 'Crédit appliqué',
  credit_pending: 'Crédit en attente',
  credit_failed: 'Crédit échoué',
  famille_activee: 'Famille inter-site activée',
  famille_locale_creee: 'Famille locale créée',
  famille_desactivee: 'Famille inter-site désactivée',
  famille_sortie_intersite_mono_site: 'Famille sortie de l’inter-site',
  famille_sortie_intersite_sans_site: 'Famille inter-site clôturée',
  famille_locale_archivee_apres_delai: 'Famille archivée après délai',
  membre_retire_synchronisation: 'Membre retiré automatiquement',
  membre_retire_transfert_eleve: 'Membre retiré par transfert',
  famille_locale_detachee_sans_membre: 'Famille locale détachée',
  entity_registered: 'Entité enregistrée'
}[action] || String(action || 'Action inter-site').replace(/_/g, ' '));

const auditPreview = details => {
  const data = parseAuditDetails(details);
  const entries = Object.entries(data).filter(([key, value]) => (
    value !== null
    && value !== undefined
    && typeof value !== 'object'
    && !AUDIT_DETAIL_HIDDEN_KEYS.has(key)
    && !/_id$/i.test(key)
  ));
  if (entries.length === 0) return 'Aucun détail complémentaire';
  return entries.slice(0, 4).map(([key, value]) => `${auditDetailLabel(key)} : ${auditDetailValue(value)}`).join(' · ');
};

const AuditTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.intersite.getAuditLog(250);
      if (r?.success) setLogs(r.data || []);
    }
    catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);
  const icon = a => {
    if (a?.includes('transfer')) return ArrowRightLeft;
    if (a?.includes('child_link') || a?.includes('liaison')) return Link2;
    if (a?.includes('transaction') || a?.includes('credit')) return CreditCard;
    if (a?.includes('entity')) return Users;
    return Activity;
  };
  const categoryOf = a => {
    if (a?.includes('transfer')) return 'transfer';
    if (a?.includes('child_link') || a?.includes('liaison')) return 'children';
    if (a?.includes('transaction') || a?.includes('credit')) return 'finance';
    if (a?.includes('famille')) return 'families';
    return 'system';
  };
  const filtered = logs.filter(log => {
    if (category !== 'all' && categoryOf(log.action) !== category) return false;
    if (!query.trim()) return true;
    const haystack = normalizeText([log.action, log.entity_type, log.entity_id, log.user_name, log.site_subdomain, auditPreview(log.details)].join(' '));
    return haystack.includes(normalizeText(query));
  });
  const counts = {
    all: logs.length,
    transfer: logs.filter(l => categoryOf(l.action) === 'transfer').length,
    children: logs.filter(l => categoryOf(l.action) === 'children').length,
    finance: logs.filter(l => categoryOf(l.action) === 'finance').length,
    families: logs.filter(l => categoryOf(l.action) === 'families').length,
    system: logs.filter(l => categoryOf(l.action) === 'system').length
  };
  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedLogs,
    pageSize
  } = usePaginatedList(filtered, PAGE_SIZE_AUDIT, `${category}|${query}`);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MetricTile label="Événements" value={counts.all} icon={History} />
        <MetricTile label="Transferts" value={counts.transfer} icon={ArrowRightLeft} tone="blue" />
        <MetricTile label="Enfants à charge" value={counts.children} icon={Link2} tone="green" />
        <MetricTile label="Finance" value={counts.finance} icon={CreditCard} tone="amber" />
      </div>

      <Surface className="p-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { k: 'all', l: 'Tout' },
              { k: 'transfer', l: 'Transferts' },
              { k: 'children', l: 'Enfants' },
              { k: 'finance', l: 'Finance' },
              { k: 'families', l: 'Familles' },
              { k: 'system', l: 'Système' },
            ].map(f => (
              <FilterButton key={f.k} active={category === f.k} onClick={() => setCategory(f.k)} count={counts[f.k]}>{f.l}</FilterButton>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SearchBox value={query} onChange={setQuery} placeholder="Rechercher dans le journal..." />
            <button
              type="button"
              onClick={loadLogs}
              disabled={loading}
              title="Actualiser le journal"
              className="h-9 w-9 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin text-gray-400' : 'text-gray-500'} />
            </button>
          </div>
        </div>
      </Surface>

      <Surface className="overflow-hidden relative">
        {loading && logs.length === 0
          ? <LoadingSpinner message="Chargement du journal d'audit…" />
          : filtered.length === 0 && !loading
          ? <EmptyState icon={History} title="Journal vide" description="Toutes les opérations inter-sites seront tracées ici automatiquement." />
          : (
            <div className="overflow-x-auto">
              <div className="px-5 py-3 border-b border-gray-50 bg-gray-50/60 flex items-center justify-between">
                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{filtered.length} événement(s) affiché(s)</span>
                <span className="text-[11px] text-gray-400">Trié du plus récent au plus ancien</span>
              </div>
              <div className="min-w-[980px]">
                <div className="grid grid-cols-[minmax(250px,1.25fr)_130px_145px_minmax(280px,1.6fr)_150px] gap-4 px-5 py-3 bg-white border-b border-gray-100">
                  {['Événement', 'Site', 'Objet', 'Détails', 'Date'].map((h, idx) => (
                    <div key={h} className={`text-[11px] font-semibold text-gray-500 uppercase tracking-wide ${idx === 4 ? 'text-right' : 'text-left'}`}>{h}</div>
                  ))}
                </div>
                <div className="divide-y divide-gray-50">
                {paginatedLogs.map((log, i) => {
                  const Icon = icon(log.action);
                  return (
                    <div key={log.id || i} className="grid grid-cols-[minmax(250px,1.25fr)_130px_145px_minmax(280px,1.6fr)_150px] gap-4 px-5 py-3 items-start hover:bg-gray-50/50 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-[#246990]/10 flex items-center justify-center flex-shrink-0">
                          <Icon size={14} className="text-[#246990]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-900">{auditLabel(log.action)}</p>
                          {log.user_name && <p className="text-[11px] text-gray-400 truncate mt-0.5">Par {log.user_name}</p>}
                        </div>
                      </div>
                      <div>{log.site_subdomain ? <SiteTag name={log.site_subdomain} /> : <span className="text-[11px] text-gray-300">—</span>}</div>
                      <span className="inline-flex w-fit px-2 py-1 rounded-md bg-gray-100 text-[10px] font-semibold text-gray-500">
                        {auditEntityLabel(log.entity_type)}
                      </span>
                      <p className="text-[11px] text-gray-500 leading-relaxed whitespace-normal break-words">{auditPreview(log.details)}</p>
                      <div className="text-right">
                        <p className="text-[11px] text-gray-400">{fmtDT(log.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          )
        }
      </Surface>
      <PaginationBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalItems={filtered.length}
        pageSize={pageSize}
        label="événement"
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MODALS (inchangées)
// ─────────────────────────────────────────────────────────────────────────────

const FL = ({ children }) => <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1.5">{children}</label>;
const FI = props => <input {...props} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#246990]/30 focus:border-[#246990] transition-all" />;
const SubmitBtn = ({ disabled, loading: ld, icon: Icon, label, loadLabel, onClick }) => (
  <button type="button" disabled={disabled || ld} onClick={onClick}
    className={`w-full h-9 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${BOUTON_PLEIN}`}>
    {ld ? <Loader2 size={14} className="animate-spin" /> : <Icon size={13} />}
    {ld ? loadLabel : label}
  </button>
);
const transferTypeLabel = type => type === 'employe'
  ? 'Employé'
  : type === 'enseignant'
    ? 'Enseignant'
    : type || 'Dossier personnel';

const DetailInfo = ({ label, value, tone = 'default' }) => {
  const tones = {
    default: 'bg-white border-gray-200 text-gray-900',
    blue: 'bg-[#246990]/5 border-[#246990]/15 text-[#246990]',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    red: 'bg-red-50 border-red-100 text-red-700',
    green: 'bg-teal-50 border-teal-100 text-teal-700'
  };
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${tones[tone] || tones.default}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide opacity-60">{label}</p>
      <p className="text-xs font-semibold mt-1 break-words">{value || '—'}</p>
    </div>
  );
};

const TimelineStep = ({ done, icon: Icon, title, date, actor }) => (
  <div className="flex items-start gap-3">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${done ? 'bg-[#246990] text-white' : 'bg-gray-100 text-gray-400'}`}>
      <Icon size={14} />
    </div>
    <div className="min-w-0 flex-1 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
      <p className={`text-xs font-semibold ${done ? 'text-gray-900' : 'text-gray-400'}`}>{title}</p>
      <p className="text-[11px] text-gray-400 mt-0.5">{date ? fmtDT(date) : 'En attente'}</p>
      {actor && <p className="text-[11px] text-gray-500 mt-1">Par {actor}</p>}
    </div>
  </div>
);

const TransferDetailPage = ({ transfer, onClose }) => {
  const source = transfer.from_site || transfer.site_source || transfer.from_site_subdomain;
  const target = transfer.to_site || transfer.site_cible || transfer.to_site_subdomain;
  const name = transfer.entity_name || transfer.nom_employe || 'Dossier personnel';
  const reference = transfer.reference || `TRF-${transfer.id}`;
  const isRejected = transfer.status === 'rejected';

  return (
    <PageDetailInterSite
      titre="Détail du transfert"
      sousTitre={`${reference} · ${name}`}
      retourLibelle="Retour aux transferts"
      icone={ArrowLeftRight}
      onRetour={onClose}
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-lg bg-[#246990] text-white flex items-center justify-center flex-shrink-0">
                <UserCheck size={22} />
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold text-gray-900 truncate">{name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{transferTypeLabel(transfer.entity_type)}</p>
              </div>
            </div>
            <StatusBadge status={transfer.status} />
          </div>

          <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div className="rounded-lg bg-white border border-gray-200 p-3 min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Site source</p>
              <p className="text-sm font-semibold text-gray-900 truncate mt-1">{siteLabel(source)}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#246990]/10 text-[#246990] flex items-center justify-center">
              <ChevronRight size={16} />
            </div>
            <div className="rounded-lg bg-white border border-gray-200 p-3 min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Site cible</p>
              <p className="text-sm font-semibold text-gray-900 truncate mt-1">{siteLabel(target)}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <DetailInfo label="Créé le" value={fmtDT(transfer.created_at)} />
          <DetailInfo label="Dernière mise à jour" value={fmtDT(transfer.updated_at)} />
          <DetailInfo label="Référence" value={reference} tone="blue" />
        </div>

        {(transfer.motif || transfer.rejection_reason) && (
          <div className={`rounded-lg border p-3 ${isRejected ? 'bg-red-50 border-red-100' : 'bg-amber-50 border-amber-100'}`}>
            <p className={`text-[11px] font-semibold uppercase tracking-wide ${isRejected ? 'text-red-500' : 'text-amber-600'}`}>
              {isRejected ? 'Motif de rejet' : 'Motif de la demande'}
            </p>
            <p className={`text-xs font-medium mt-1 leading-relaxed ${isRejected ? 'text-red-700' : 'text-amber-800'}`}>
              {transfer.rejection_reason || transfer.motif}
            </p>
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-semibold text-gray-900">Circuit de validation</p>
            <span className="text-[11px] text-gray-400">Personnel & enseignants</span>
          </div>
          <div className="space-y-3">
            <TimelineStep done icon={Plus} title="Demande créée" date={transfer.created_at} actor={transfer.created_by_nom || transfer.created_by_name} />
            <TimelineStep
              done={['approved_source', 'approved_both', 'executed'].includes(transfer.status)}
              icon={Check}
              title="Départ validé"
              date={transfer.approved_source_at}
              actor={transfer.approved_source_by_name || transfer.approved_source_by}
            />
            <TimelineStep
              done={['approved_both', 'executed'].includes(transfer.status)}
              icon={ShieldCheck}
              title="Arrivée validée"
              date={transfer.approved_target_at}
              actor={transfer.approved_target_by_name || transfer.approved_target_by}
            />
            <TimelineStep
              done={transfer.status === 'executed'}
              icon={Send}
              title="Transfert exécuté"
              date={transfer.executed_at}
              actor={transfer.executed_by_name || transfer.executed_by}
            />
          </div>
        </div>
      </div>
    </PageDetailInterSite>
  );
};

const RejectActionModal = ({ title, subtitle, loading, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const canSubmit = reason.trim().length >= 3;
  return (
    <ModalWrapper title={title} subtitle={subtitle} onClose={onClose}>
      <div className="space-y-4">
        <div className="p-3 rounded-lg bg-red-50 border border-red-100 flex items-start gap-2">
          <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-700 font-medium">
            Indiquez un motif clair. Il sera conservé dans le journal d'audit et visible dans l'historique de la demande.
          </p>
        </div>
        <div>
          <FL>Motif de rejet</FL>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={4}
            autoFocus
            placeholder="Ex. Dossier incomplet, doublon détecté, validation administrative manquante..."
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#246990]/30 focus:border-[#246990] resize-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className={`${BOUTON_PIED} ${BOUTON_SECONDAIRE}`}>
            Annuler
          </button>
          <button
            type="button"
            disabled={!canSubmit || loading}
            onClick={() => onConfirm(reason.trim())}
            className={`${BOUTON_PIED} ${BOUTON_DANGER}`}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <X size={13} />}
            Rejeter
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

/**
 * Clôture d'une liaison enfant à charge, précédée de son analyse.
 *
 * Le dialogue ne demande pas « êtes-vous sûr ? » — question à laquelle personne
 * ne peut répondre utilement. Il montre l'état réel des deux côtés de la
 * liaison, puis en tire la conséquence : sans effet, ou interruption d'un
 * versement en cours.
 */
const ClotureLiaisonModal = ({ liaison, analyse, loading, onClose, onConfirm }) => {
  const d = analyse?.donnees;
  const avecImpact = d?.verdict === 'avec_impact';
  const indetermine = d?.verdict === 'indetermine';

  return (
    <ModalWrapper
      title="Terminer la liaison"
      subtitle={liaison.child_name || liaison.nom_eleve || 'Enfant à charge'}
      onClose={onClose}
    >
      <div className="space-y-4">
        {analyse?.chargement && (
          <div className="flex items-center gap-2 p-3 text-xs text-gray-500">
            <Loader2 size={14} className="animate-spin" />
            Vérification de l'état des deux sites…
          </div>
        )}

        {analyse?.erreur && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-100 flex items-start gap-2">
            <AlertTriangle size={14} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 font-medium">{analyse.erreur}</p>
          </div>
        )}

        {d && (
          <>
            <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
              {d.constats.map((c, i) => (
                <p key={i} className="px-3 py-2 text-xs text-gray-700 leading-snug">{c}</p>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Retenue mensuelle</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{fmt(d.montant_mensuel)}</p>
              </div>
              <div className="rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Déjà crédité</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">
                  {fmt(d.mouvements.total_credite)}
                  <span className="ml-1 text-[11px] font-medium text-gray-400">
                    ({d.mouvements.nombre} mouvement{d.mouvements.nombre > 1 ? 's' : ''})
                  </span>
                </p>
              </div>
            </div>

            <div className={`p-3 rounded-lg border flex items-start gap-2 ${avecImpact
              ? 'bg-red-50 border-red-100'
              : indetermine ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-200'}`}>
              {avecImpact
                ? <AlertTriangle size={14} className="text-red-600 mt-0.5 flex-shrink-0" />
                : indetermine
                  ? <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  : <CheckCircle2 size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />}
              <div>
                <p className={`text-xs font-semibold ${avecImpact ? 'text-red-700' : indetermine ? 'text-amber-700' : 'text-gray-700'}`}>
                  {avecImpact ? 'Cette clôture a un effet réel' : indetermine ? 'Effet indéterminé' : 'Clôture sans effet'}
                </p>
                <p className={`text-xs mt-0.5 leading-snug ${avecImpact ? 'text-red-700' : indetermine ? 'text-amber-700' : 'text-gray-600'}`}>
                  {d.consequence}
                </p>
              </div>
            </div>

            <p className="text-[11px] text-gray-500 leading-snug">
              Les mouvements déjà enregistrés sont conservés. La clôture est définitive : une liaison
              terminée ne se réactive pas, il faut en créer une nouvelle.
            </p>
          </>
        )}

        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose}
            className={`${BOUTON_PIED} ${BOUTON_SECONDAIRE}`}>
            Annuler
          </button>
          <button
            type="button"
            disabled={loading || !d || !d.peut_etre_ferme}
            onClick={() => onConfirm(
              d?.verdict === 'avec_impact'
                ? 'Terminée manuellement alors que la liaison était encore active des deux côtés'
                : 'Terminée manuellement : la liaison ne désignait plus un élève et un employé actifs'
            )}
            className={`flex-1 py-2.5 rounded-lg text-white text-xs font-semibold disabled:opacity-40 flex items-center justify-center gap-2 ${avecImpact ? 'bg-red-600 hover:bg-red-700' : 'bg-[#246990] hover:bg-[#1f5a7a]'}`}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={13} />}
            {avecImpact ? 'Terminer malgré tout' : 'Terminer la liaison'}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

// `danger` habille la confirmation pour une action irreversible : le vert de la
// variante par defaut annonce une bonne nouvelle, ce qui n'est pas le cas quand
// on ferme definitivement une liaison.
const ConfirmActionModal = ({ title, subtitle, description, confirmLabel, loading, icon: Icon = CheckCircle2, danger = false, onClose, onConfirm }) => (
  <ModalWrapper title={title} subtitle={subtitle} onClose={onClose}>
    <div className="space-y-4">
      <div className={`p-3 rounded-lg border flex items-start gap-2 ${danger ? 'bg-red-50 border-red-100' : 'bg-teal-50 border-teal-100'}`}>
        <Icon size={14} className={`mt-0.5 flex-shrink-0 ${danger ? 'text-red-600' : 'text-teal-600'}`} />
        <p className={`text-xs font-medium ${danger ? 'text-red-700' : 'text-teal-700'}`}>{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={onClose} className={`${BOUTON_PIED} ${BOUTON_SECONDAIRE}`}>
          Annuler
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={onConfirm}
          className={`flex-1 py-2.5 rounded-lg text-white text-xs font-semibold disabled:opacity-40 flex items-center justify-center gap-2 ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-600 hover:bg-teal-700'}`}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Icon size={13} />}
          {confirmLabel}
        </button>
      </div>
    </div>
  </ModalWrapper>
);

const CreateTransferModal = ({ siblings, onClose, onCreated }) => {
  const [form, setForm] = useState({ entity_type: 'employe', entity_id: '', target_site: '', motif: '' });
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sub, setSub] = useState(false);
  const [notice, setNotice] = useState(null);
  const targetSite = siblings.find(s => (s.subdomain || s.tenant_db || s.site_name) === form.target_site);

  const search = async () => {
    if (query.trim().length < 2) {
      setNotice({ type: 'info', message: 'Saisissez au moins 2 caractères pour lancer la recherche.' });
      return;
    }
    setSearching(true);
    setNotice(null);
    try {
      const r = await api.intersite.searchEntities(form.entity_type, query.trim());
      if (!r?.success) {
        setResults([]);
        setNotice({ type: 'error', message: getApiError(r, 'Recherche impossible') });
        return;
      }
      setResults(r.data || []);
      if ((r.data || []).length === 0) {
        setNotice({ type: 'info', message: 'Aucun employé actif trouvé pour cette recherche.' });
      }
    } catch (e) {
      setNotice({ type: 'error', message: e.message || 'Erreur lors de la recherche' });
    } finally {
      setSearching(false);
    }
  };

  const selectEmployee = (employee) => {
    setSelectedEmployee(employee);
    setForm({ ...form, entity_id: employee.id || employee.local_id });
    setQuery(employee.nom || employee.name || '');
    setResults([]);
    setNotice(null);
  };

  const changeType = (entityType) => {
    setForm({ ...form, entity_type: entityType, entity_id: '' });
    setSelectedEmployee(null);
    setResults([]);
    setNotice(null);
  };

  const submit = async () => {
    if (!selectedEmployee || !form.entity_id) {
      setNotice({ type: 'error', message: 'Sélectionnez un employé dans la liste de résultats.' });
      return;
    }
    if (!form.target_site) {
      setNotice({ type: 'error', message: 'Sélectionnez le site de destination.' });
      return;
    }
    setSub(true);
    setNotice(null);
    try {
      const r = await api.intersite.createTransfer(form);
      if (r?.success) {
        onCreated('Demande de transfert envoyée avec succès.');
        return;
      }
      setNotice({ type: 'error', message: getApiError(r, "La demande n'a pas pu être envoyée") });
    } catch (e) {
      setNotice({ type: 'error', message: e.message || "Erreur lors de l'envoi de la demande" });
    } finally {
      setSub(false);
    }
  };
  return (
    <ModalWrapper taille="formulaire" icone={Send} title="Nouveau transfert" subtitle="Initier une demande de transfert personnel" onClose={onClose}>
      <div className="space-y-4">
        {notice && (
          <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${notice.type === 'error'
            ? 'bg-red-50 border-red-100 text-red-700'
            : 'bg-blue-50 border-blue-100 text-blue-700'
            }`}>
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
            <span className="font-medium">{notice.message}</span>
          </div>
        )}

        <div>
          <FL>Type de dossier</FL>
          <div className="grid grid-cols-2 gap-2">
            {[{ v: 'employe', l: 'Employé', icon: UserCheck }, { v: 'enseignant', l: 'Enseignant', icon: GraduationCap }].map(t => (
              <button key={t.v} type="button" onClick={() => changeType(t.v)}
                className={`flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all ${form.entity_type === t.v ? 'bg-[#246990] text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                <t.icon size={13} />{t.l}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <FL>Employé à transférer</FL>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
              <FI value={query}
                onChange={e => { setQuery(e.target.value); setSelectedEmployee(null); setResults([]); setForm({ ...form, entity_id: '' }); }}
                onKeyDown={e => e.key === 'Enter' && search()}
                placeholder="Nom ou prénom..."
                style={{ paddingLeft: 34 }}
              />
            </div>
            <button type="button" onClick={search} disabled={searching}
              className="w-10 h-9 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 flex items-center justify-center disabled:opacity-50">
              {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={13} className="text-gray-500" />}
            </button>
          </div>

          {selectedEmployee && (
            <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-teal-50 border border-teal-200 rounded-lg">
              <CheckCircle2 size={13} className="text-teal-600" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-teal-800 truncate">{selectedEmployee.nom || selectedEmployee.name}</p>
                <p className="text-[11px] text-teal-600">{selectedEmployee.poste || 'Personnel actif'}</p>
              </div>
              <button type="button" onClick={() => { setSelectedEmployee(null); setForm({ ...form, entity_id: '' }); }}
                className="w-6 h-6 rounded-md hover:bg-teal-100 flex items-center justify-center">
                <X size={12} className="text-teal-700" />
              </button>
            </div>
          )}

          {results.length > 0 && !selectedEmployee && (
            <div className="absolute z-50 w-full mt-1.5 border border-gray-200 rounded-lg divide-y divide-gray-50 max-h-44 overflow-y-auto bg-white shadow-lg">
              {results.map((r, i) => (
                <button key={r.id || i} type="button" onClick={() => selectEmployee(r)}
                  className="w-full px-3 py-2.5 text-left text-xs hover:bg-gray-50 transition-colors flex items-center justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block font-semibold text-gray-800 truncate">{r.nom || r.name}</span>
                    <span className="block text-[11px] text-gray-400 truncate">{r.poste || 'Personnel actif'}</span>
                  </span>
                  <ChevronRight size={12} className="text-gray-300 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <FL>Site de destination</FL>
          <SearchableSelect
            value={form.target_site}
            onChange={value => setForm({ ...form, target_site: value })}
            options={siblings}
            placeholder="Sélectionner le site cible..."
            searchPlaceholder="Rechercher un site..."
            emptyLabel="Aucun site trouvé"
            getOptionValue={site => site.subdomain || site.tenant_db || site.site_name}
            getOptionLabel={site => site.nom || site.site_name || site.subdomain}
            getOptionMeta={site => site.subdomain || site.tenant_db}
          />
        </div>
        <div><FL>Motif</FL><textarea value={form.motif} onChange={e => setForm({ ...form, motif: e.target.value })} rows={3} placeholder="Ex. Affectation administrative, besoin du site cible..." className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#246990]/30 focus:border-[#246990] resize-none" /></div>

        {(selectedEmployee || targetSite) && (
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Résumé de la demande</p>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-3"><span className="text-[11px] text-gray-500">Personnel</span><span className="text-[11px] font-semibold text-gray-800 text-right truncate">{selectedEmployee?.nom || selectedEmployee?.name || 'Non sélectionné'}</span></div>
              <div className="flex items-center justify-between gap-3"><span className="text-[11px] text-gray-500">Destination</span><span className="text-[11px] font-semibold text-gray-800 text-right truncate">{targetSite?.nom || targetSite?.site_name || 'Non sélectionnée'}</span></div>
            </div>
          </div>
        )}

        <SubmitBtn disabled={!selectedEmployee || !form.entity_id || !form.target_site} loading={sub} icon={Send} label="Envoyer la demande" loadLabel="Envoi..." onClick={submit} />
      </div>
    </ModalWrapper>
  );
};

const CreateChildLinkModal = ({ siblings, currentSite, onClose, onCreated }) => {
  const [form, setForm] = useState({ employe_id: '', target_site: '', eleve_id: '' });
  const [sub, setSub] = useState(false);
  const [pQ, setPQ] = useState(''); const [pR, setPR] = useState([]); const [pS, setPS] = useState(null); const [pDD, setPDD] = useState(false); const [pL, setPL] = useState(false);
  const [cQ, setCQ] = useState(''); const [cR, setCR] = useState([]); const [cS, setCS] = useState(null); const [cDD, setCDD] = useState(false); const [cL, setCL] = useState(false);
  useEffect(() => { if (pQ.length < 2) { setPR([]); return; } const t = setTimeout(async () => { setPL(true); try { const r = await api.intersite.searchEntities('employe', pQ); if (r?.success) setPR(r.data || []); } catch (e) { console.error(e); } setPL(false); }, 300); return () => clearTimeout(t); }, [pQ]);
  useEffect(() => { if (cQ.length < 2 || !form.target_site) { setCR([]); return; } const t = setTimeout(async () => { setCL(true); try { const r = await api.intersite.searchRemoteEntities(form.target_site, 'eleve', cQ); if (r?.success) setCR(r.data || []); } catch (e) { console.error(e); } setCL(false); }, 300); return () => clearTimeout(t); }, [cQ, form.target_site]);
  const selP = p => { setPS(p); setForm({ ...form, employe_id: p.id.toString() }); setPQ(p.nom || p.name); setPDD(false); };
  const selC = c => { setCS(c); setForm({ ...form, eleve_id: c.id.toString() }); setCQ(c.full_name || `${c.prenom || ''} ${c.nom}`.trim()); setCDD(false); };
  const frais = cS ? parseFloat(cS.frais_mensuel || 0) : 0;
  const submit = async () => { if (!form.employe_id || !form.target_site || !form.eleve_id) return; setSub(true); try { const r = await api.intersite.createChildLink({ ...form, montant_mensuel: frais }); if (r?.success) onCreated(); } catch (e) { console.error(e); } setSub(false); };
  return (
    <ModalWrapper taille="formulaire" icone={Link2} title="Nouvelle liaison" subtitle="Lier un parent à un enfant scolarisé sur un autre site" onClose={onClose}>
      <div className="space-y-4">
        <div className="relative"><FL>Parent (employé sur ce site)</FL><div className="relative"><Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" /><FI value={pQ} onChange={e => { setPQ(e.target.value); setPDD(true); setPS(null); setForm({ ...form, employe_id: '' }); }} placeholder="Tapez le nom…" style={{ paddingLeft: 32 }} />{pL && <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-300" />}</div>{pS && <div className="flex items-center gap-2 px-2.5 py-1.5 mt-1 bg-blue-50 border border-blue-200 rounded-lg"><CheckCircle2 size={11} className="text-blue-500" /><span className="text-[11px] font-semibold text-blue-700">{pS.nom || pS.name}</span><span className="text-[11px] text-blue-400">· {pS.poste || 'Employé'}</span></div>}{pDD && pR.length > 0 && !pS && <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">{pR.map((p, i) => <button key={p.id || i} onClick={() => selP(p)} className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 text-xs"><p className="font-semibold text-gray-800">{p.nom || p.name}</p><p className="text-gray-400">{p.poste || 'Employé'}</p></button>)}</div>}</div>
        <div>
          <FL>Site de l'enfant</FL>
          <SearchableSelect
            value={form.target_site}
            onChange={value => { setForm({ ...form, target_site: value, eleve_id: '' }); setCS(null); setCQ(''); setCR([]); }}
            options={siblings}
            placeholder="Sélectionner le site..."
            searchPlaceholder="Rechercher un site..."
            emptyLabel="Aucun site trouvé"
            getOptionValue={site => site.subdomain || site.tenant_db || site.site_name}
            getOptionLabel={site => site.nom || site.site_name || site.subdomain}
            getOptionMeta={site => site.subdomain || site.tenant_db}
          />
        </div>
        <div className="relative"><FL>Enfant (élève sur le site cible)</FL>{!form.target_site ? <div className="px-3 py-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 text-[11px] text-gray-400 italic">Sélectionnez d'abord le site de l'enfant</div> : <><div className="relative"><Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" /><FI value={cQ} onChange={e => { setCQ(e.target.value); setCDD(true); setCS(null); setForm({ ...form, eleve_id: '' }); }} placeholder="Tapez le nom…" style={{ paddingLeft: 32 }} />{cL && <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-300" />}</div>{cS && <div className="flex items-center gap-2 px-2.5 py-1.5 mt-1 bg-teal-50 border border-teal-200 rounded-lg"><CheckCircle2 size={11} className="text-teal-500" /><span className="text-[11px] font-semibold text-teal-700">{cS.full_name || `${cS.prenom} ${cS.nom}`}</span>{cS.classe_nom && <span className="text-[11px] text-teal-400">· {cS.classe_nom}</span>}<span className="text-[11px] font-semibold text-teal-600 ml-auto">{fmt(frais)}</span></div>}{cDD && cR.length > 0 && !cS && <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">{cR.map((c, i) => <button key={c.id || i} onClick={() => selC(c)} className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 flex items-center justify-between text-xs"><div><p className="font-semibold text-gray-800">{c.full_name || `${c.prenom || ''} ${c.nom}`.trim()}</p><p className="text-gray-400">{c.classe_nom || ''}</p></div><div className="text-right"><p className="font-semibold text-[#246990]">{fmt(parseFloat(c.frais_mensuel || 0))}</p><p className="text-gray-400 text-[11px]">par mois</p></div></button>)}</div>}</>}</div>
        {cS && <div className="p-3 bg-blue-50 rounded-lg border border-blue-100"><FL>Frais mensuels (automatique)</FL><div className="flex items-center gap-2"><CreditCard size={14} className="text-[#3084b5]" /><span className="text-sm font-semibold text-gray-800">{fmt(frais)}</span><span className="text-[11px] text-gray-400">/ mois</span>{frais === 0 && <span className="text-[11px] text-orange-500 font-semibold ml-auto">Aucun frais</span>}</div></div>}
        {pS && cS && <div className="p-3 bg-gray-50 rounded-lg border border-gray-100"><p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Récapitulatif</p><div className="flex items-center gap-2 text-xs"><span className="font-semibold text-gray-800">{pS.nom || pS.name}</span><ChevronRight size={11} className="text-gray-300" /><span className="font-semibold text-gray-800">{cS.full_name || cS.nom}</span></div><p className="text-[11px] text-gray-500 mt-1">Retenue de <span className="font-semibold text-gray-700">{fmt(frais)}</span>/mois</p></div>}
        <SubmitBtn disabled={!form.employe_id || !form.target_site || !form.eleve_id} loading={sub} icon={Link2} label="Créer la liaison" loadLabel="Création…" onClick={submit} />
      </div>
    </ModalWrapper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
const InterSiteManagement = () => {
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [group, setGroup] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [currentSite, setCurrentSite] = useState('');
  const [transfers, setTransfers] = useState([]);
  const [eleveTransfers, setEleveTransfers] = useState([]);
  const [childLinks, setChildLinks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const status = await api.intersite.getStatus();
      if (!status?.success) {
        setError(status?.message || status?.error || 'Impossible de vérifier le statut inter-sites.');
        setIsEnabled(false);
        setLoading(false);
        return;
      }
      if (!status?.data?.is_member) { setIsEnabled(false); setLoading(false); return; }
      setIsEnabled(true);
      setCurrentSite(status.data.current_site || status.data.site_name || '');
      const [g, s, t, tx, cl, et] = await Promise.all([
        api.intersite.getGroup(), api.intersite.getSiblings(), api.intersite.getTransfers(),
        api.intersite.getTransactionHistory(), api.intersite.getAllChildLinks(),
        api.intersite.getEleveTransfers(),
      ]);
      if (g?.success) setGroup(g.data);
      if (s?.success) setSiblings(s.data || []);
      if (t?.success) setTransfers(t.data || []);
      if (tx?.success) setTransactions(tx.data || []);
      if (cl?.success) setChildLinks(cl.data || []);
      if (Array.isArray(et)) setEleveTransfers(et);
      else if (et?.success) setEleveTransfers(et.data || []);
    } catch (e) { console.error(e); setError('Impossible de charger les données inter-sites.'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const pendingTransfers = transfers.filter(t => ['pending', 'approved_source', 'approved_both'].includes(t.status)).length
    + eleveTransfers.filter(t => ['pending', 'approved_source', 'approved_both'].includes(t.statut)).length;
  const activeLinks = childLinks.filter(l => l.statut === 'active').length;
  const totalTransacted = transactions.filter(t => t.status === 'completed').reduce((s, t) => s + (t.montant || 0), 0);
  const sectionCounts = {
    transfers: pendingTransfers,
    children: childLinks.filter(l => l.statut === 'suspended' && l.restoration_detected).length,
  };
  const activeCard = SECTIONS.find(s => s.id === activeSection);

  // Premier chargement : le module affichait sa page immediatement, avec des
  // « ... » a la place des chiffres, puis tout apparaissait d'un coup. Les 26
  // autres ecrans de l'application montrent un LoadingSpinner centre — c'est ce
  // decalage d'un module a l'autre qui se voyait.
  if (loading && !group && !error) {
    return <LoadingSpinner message="Chargement du module Inter-Sites…" size="lg" />;
  }

  if (!loading && !isEnabled && !error) return (
    <div className="p-8 w-full flex items-center justify-center min-h-96">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-lg bg-gray-50 flex items-center justify-center mx-auto mb-5">
          <Globe size={30} className="text-gray-300" />
        </div>
        <h2 className="text-base font-semibold text-gray-800 mb-2">Module Inter-Sites non activé</h2>
        <p className="text-xs text-gray-400 mb-6 leading-relaxed">Ce site n'est pas rattaché à un groupe. Contactez l'administrateur Hub.</p>
        <button onClick={() => navigate('/administration')}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#246990] text-white text-xs font-semibold rounded-lg hover:bg-[#1f5a7a] transition-colors">
          <ChevronLeft size={13} />Retour à l'accueil
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 w-full space-y-5">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between gap-4">

        <div>

          <h1 className="text-3xl font-bold mb-6 text-[#246990]">Liaison Inter-Sites</h1>

        </div>

        <button
          type="button"
          onClick={() => setIsGuideOpen(true)}
          className="mb-6 inline-flex items-center gap-2 rounded-lg border border-[#246990]/20 bg-[#246990]/10 px-3 py-2 text-xs font-semibold text-[#246990] transition hover:bg-[#246990]/15"
        >
          <BookOpenText size={15} />
          Guide d'utilisation
        </button>

      </div>

      {/* ── ERREUR ── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-100 rounded-lg">
            <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
            <p className="text-xs font-medium text-red-600">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto"><X size={13} className="text-red-300 hover:text-red-500" /></button>
        </div>
      )}

      {/* ── LAYOUT 2 COLONNES ── */}
      <div className="flex flex-col xl:flex-row gap-5 items-start">

        {/* ── COLONNE GAUCHE ── */}
        <div className="flex-1 min-w-0 space-y-5">

          {/* STAT CARDS — compactes (p-4, text-2xl), alignées avec les section cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard title="Sites connectés" value={loading ? '...' : siblings.length + 1} detail="Sites du groupe" icon={Signal} loading={loading} color="from-[#246990] to-[#3084b5]" />
            <StatCard title="Transferts actifs" value={loading ? '...' : pendingTransfers} detail="Demandes à traiter" icon={ArrowRightLeft} loading={loading} color="from-[#c66d59] to-[#ae7467]" />
            <StatCard title="Liaisons actives" value={loading ? '...' : activeLinks} detail="Relations inter-sites" icon={Link2} loading={loading} color="from-[#8B5CF6] to-[#A855F7]" />
            <StatCard title="Volume transacté" value={loading ? '...' : fmt(totalTransacted)} detail="Transactions validées" icon={Banknote} loading={loading} color="from-[#319190] to-[#63b5b4]" />
          </div>

          {/* CONTENU */}
          <>
            {!activeSection && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                  {SECTIONS.map(sec => (
                    <SectionCard key={sec.id} section={sec} onClick={() => setActiveSection(sec.id)} extraCount={sectionCounts[sec.id]} />
                  ))}
                </div>
                <OverviewPanel group={group} siblings={siblings} currentSite={currentSite} transfers={transfers} eleveTransfers={eleveTransfers} transactions={transactions} />
              </div>
            )}

            {activeSection && (
              <div>
                <DrillInHeader section={activeCard} onBack={() => setActiveSection(null)} />
                {activeSection === 'transfers' && <TransfersSectionContent transfers={transfers} siblings={siblings} currentSite={currentSite} onRefresh={loadData} />}
                {activeSection === 'children' && <ChildrenSectionContent childLinks={childLinks} transactions={transactions} siblings={siblings} currentSite={currentSite} onRefresh={loadData} />}
                {activeSection === 'familles' && <InterSiteFamillesDashboard currentSite={currentSite} />}
                {activeSection === 'coherence' && (
                  <div className="space-y-4">
                    <CoherenceInterSitePanel />
                    {/* Familles d'un côté, transferts et enfants à charge de
                        l'autre : trois mécanismes différents, un seul endroit
                        où l'on vient vérifier que les sites racontent la même
                        histoire. */}
                    <CoherenceTransfertsPanel />
                  </div>
                )}
                {activeSection === 'audit' && <AuditTab />}
              </div>
            )}
          </>
        </div>

      </div>
      <GuideUtilisationDrawer
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        module="liaisonInterSites"
      />
    </div>
  );
};

export default InterSiteManagement;
